import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Heading, Table, Thead, Tbody, Tr, Th, Td,
  Badge, IconButton, Spinner, HStack, Text, Accordion,
  AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';

export default function ActiveSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('meet_token');
  const headers = { 'Authorization': `Token ${token}` };

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/sessions/?active=true', { headers });
      if (res.ok) setSessions(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 15000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="gray.700">Active Sessions</Heading>
        <IconButton icon={<FiRefreshCw />} onClick={fetchSessions} variant="ghost" aria-label="Refresh" />
      </Flex>

      {loading ? (
        <Flex justify="center" py={12}><Spinner size="lg" color="teal.500" /></Flex>
      ) : sessions.length === 0 ? (
        <Box bg="white" borderRadius="xl" boxShadow="sm" p={12} textAlign="center">
          <Text color="gray.400" fontSize="lg">No active sessions</Text>
          <Text color="gray.300" fontSize="sm" mt={2}>Sessions will appear here when rooms are in use</Text>
        </Box>
      ) : (
        <Accordion allowMultiple>
          {sessions.map((session) => (
            <AccordionItem key={session.id} mb={3} border="none">
              <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
                <AccordionButton py={4} px={6} _hover={{ bg: 'gray.50' }}>
                  <Flex flex="1" align="center" gap={4}>
                    <Badge colorScheme="green" variant="solid" borderRadius="full" px={2}>LIVE</Badge>
                    <Text fontWeight="600">Session {session.session_id.substring(0, 8)}...</Text>
                    <Badge colorScheme="teal">{session.participant_count} participants</Badge>
                    <Text fontSize="xs" color="gray.400" ml="auto">
                      Started {new Date(session.started_at).toLocaleTimeString()}
                    </Text>
                  </Flex>
                  <AccordionIcon ml={4} />
                </AccordionButton>
                <AccordionPanel pb={4} px={6}>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>User</Th>
                        <Th>Role</Th>
                        <Th>Joined</Th>
                        <Th>Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {session.participants.map((p) => (
                        <Tr key={p.id}>
                          <Td fontWeight="500">{p.display_name || p.user_id}</Td>
                          <Td><Badge colorScheme={p.role === 'host' ? 'purple' : 'gray'}>{p.role}</Badge></Td>
                          <Td fontSize="xs" color="gray.500">{new Date(p.joined_at).toLocaleTimeString()}</Td>
                          <Td>
                            <Badge colorScheme={p.left_at ? 'red' : 'green'}>
                              {p.left_at ? 'Left' : 'Active'}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </AccordionPanel>
              </Box>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </Box>
  );
}
